import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input, Select, Textarea } from '@artosera/ui';
import {
  TYPES_CAISSE,
  ETATS_CAISSE,
  TYPES_LIEU,
  type TypeCaisse,
  type EtatCaisse,
  type DimensionsCaisse as Dimensions,
} from '@artosera/shared';
import {
  reserveApi,
  useCaisses,
  useMutationReserve,
  type FiltresCaisses,
} from '../../api/reserve.js';
import { estApiError } from '../../api/errors.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { Field } from '../Field.js';
import { Destination } from './Destination.js';
import { DimensionsCaisse, dimensionsCompletes, dimensionsValides } from './DimensionsCaisse.js';
import styles from './InventairePage.module.css';
import { Verrouillable } from '../module/LectureSeule.js';

export function CaissesPage() {
  const { t } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const navigate = useNavigate();
  const [filtres, setFiltres] = useState<FiltresCaisses>({ page: 1 });
  const liste = useCaisses(galerieSlug, filtres);
  const [ouvert, setOuvert] = useState(false);
  const [type, setType] = useState<TypeCaisse>('caisse_bois');
  const [interieur, setInterieur] = useState<Partial<Dimensions>>({});
  const [exterieur, setExterieur] = useState<Partial<Dimensions>>({});
  const [lieuId, setLieuId] = useState('');
  const [emplacementId, setEmplacementId] = useState('');
  const [notes, setNotes] = useState('');
  const creer = useMutationReserve(galerieSlug, (_: void, appel) =>
    reserveApi.creerCaisse(
      galerieSlug,
      {
        type,
        dimensionsInterieures: dimensionsCompletes(interieur),
        dimensionsExterieures: dimensionsCompletes(exterieur),
        lieuId: lieuId || null,
        emplacementId: emplacementId || null,
        notes,
      },
      appel,
    ),
  );
  function filtrer(patch: Partial<FiltresCaisses>) {
    setFiltres({ ...filtres, ...patch, page: 1 });
  }
  return (
    <div className={styles.page}>
      <header className={styles.entete}>
        <h2>{t('regie:inventaire.caisses')}</h2>
        <Verrouillable
          action="inventaire:write"
          ouvert={galerieALaction(galerie, 'inventaire:write')}
        >
          <Button onClick={() => setOuvert(true)}>{t('regie:inventaire.nouvelleCaisse')}</Button>
        </Verrouillable>
      </header>
      <div className={styles.formulaire} data-ui="filters">
        <Field htmlFor="caisses-recherche" label={t('regie:inventaire.rechercher')}>
          <Input
            id="caisses-recherche"
            value={filtres.q ?? ''}
            onChange={(e) => filtrer({ q: e.target.value })}
          />
        </Field>
        <Field htmlFor="caisses-etat" label={t('regie:inventaire.etat')}>
          <Select
            id="caisses-etat"
            value={filtres.etat ?? 'tous'}
            onValueChange={(v) => filtrer({ etat: v === 'tous' ? undefined : (v as EtatCaisse) })}
            options={[
              { value: 'tous', label: t('regie:inventaire.tous') },
              ...ETATS_CAISSE.map((v) => ({
                value: v,
                label: t(`regie:inventaire.etatsCaisse.${v}`),
              })),
            ]}
          />
        </Field>
        <Field htmlFor="caisses-type" label={t('regie:inventaire.type')}>
          <Select
            id="caisses-type"
            value={filtres.type ?? 'tous'}
            onValueChange={(v) => filtrer({ type: v === 'tous' ? undefined : (v as TypeCaisse) })}
            options={[
              { value: 'tous', label: t('regie:inventaire.tous') },
              ...TYPES_CAISSE.map((v) => ({
                value: v,
                label: t(`regie:inventaire.typesCaisse.${v}`),
              })),
            ]}
          />
        </Field>
        <Field htmlFor="caisses-lieu" label={t('regie:inventaire.typeLieu')}>
          <Select
            id="caisses-lieu"
            value={filtres.typeLieu ?? 'tous'}
            onValueChange={(v) => filtrer({ typeLieu: v === 'tous' ? undefined : v })}
            options={[
              { value: 'tous', label: t('regie:inventaire.tous') },
              ...TYPES_LIEU.map((v) => ({ value: v, label: t(`regie:lieux.types.${v}`) })),
            ]}
          />
        </Field>
        <Field htmlFor="caisses-dimension" label={t('regie:inventaire.dimensionMin')}>
          <Input
            id="caisses-dimension"
            type="number"
            min="0"
            value={filtres.dimensionMinCm ?? ''}
            onChange={(e) =>
              filtrer({ dimensionMinCm: e.target.value ? Number(e.target.value) : undefined })
            }
          />
        </Field>
      </div>
      <Destination
        galerie={galerieSlug}
        lieuId={filtres.lieuId ?? ''}
        emplacementId={filtres.emplacementId ?? ''}
        onChange={(lieu, emplacement) =>
          filtrer({ lieuId: lieu || undefined, emplacementId: emplacement || undefined })
        }
      />
      {liste.isPending && <p role="status">{t('commun:chargement')}</p>}
      {liste.isError && <p role="alert">{t('commun:erreurReseau')}</p>}
      {liste.data?.caisses.length === 0 && <p>{t('regie:inventaire.aucuneCaisse')}</p>}
      <div className={styles.liste}>
        {liste.data?.caisses.map((c) => (
          <Link
            className={styles.ligne}
            key={c.id}
            to={`/${galerieSlug}/inventaire/caisses/${c.id}`}
          >
            <strong>{c.numero}</strong>
            <span>
              {t(`regie:inventaire.typesCaisse.${c.type}`)} ·{' '}
              {t(`regie:inventaire.etatsCaisse.${c.etat}`)}
            </span>
          </Link>
        ))}
      </div>
      {liste.data && liste.data.pagination.pages > 1 && (
        <nav className={styles.actions} aria-label={t('regie:inventaire.pagination')}>
          <Button
            disabled={filtres.page === 1}
            onClick={() => setFiltres({ ...filtres, page: (filtres.page ?? 1) - 1 })}
          >
            {t('regie:inventaire.precedent')}
          </Button>
          <span>
            {filtres.page} / {liste.data.pagination.pages}
          </span>
          <Button
            disabled={(filtres.page ?? 1) >= liste.data.pagination.pages}
            onClick={() => setFiltres({ ...filtres, page: (filtres.page ?? 1) + 1 })}
          >
            {t('regie:inventaire.suivant')}
          </Button>
        </nav>
      )}
      <Dialog
        open={ouvert}
        onOpenChange={setOuvert}
        title={t('regie:inventaire.nouvelleCaisse')}
        closeLabel={t('commun:actions.fermer')}
      >
        <div className={styles.formulaire}>
          <Field htmlFor="caisse-nouveau-type" label={t('regie:inventaire.type')}>
            <Select
              id="caisse-nouveau-type"
              value={type}
              onValueChange={(v) => setType(v as TypeCaisse)}
              options={TYPES_CAISSE.map((v) => ({
                value: v,
                label: t(`regie:inventaire.typesCaisse.${v}`),
              }))}
            />
          </Field>
          <DimensionsCaisse
            titre={t('regie:inventaire.interieur')}
            valeur={interieur}
            onChange={setInterieur}
          />
          <DimensionsCaisse
            titre={t('regie:inventaire.exterieur')}
            valeur={exterieur}
            onChange={setExterieur}
          />
          <Destination
            galerie={galerieSlug}
            lieuId={lieuId}
            emplacementId={emplacementId}
            onChange={(lieu, emplacement) => {
              setLieuId(lieu);
              setEmplacementId(emplacement);
            }}
          />
          <Field htmlFor="caisse-nouveau-notes" label={t('regie:inventaire.note')}>
            <Textarea
              id="caisse-nouveau-notes"
              maxLength={5000}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Field>
          <Button
            disabled={
              creer.isPending || !dimensionsValides(interieur) || !dimensionsValides(exterieur)
            }
            onClick={() =>
              creer.mutate(undefined, {
                onSuccess: ({ caisse }) =>
                  navigate(`/${galerieSlug}/inventaire/caisses/${caisse.id}`),
              })
            }
          >
            {t('regie:inventaire.creer')}
          </Button>
          {creer.error && (
            <p role="alert">
              {estApiError(creer.error) ? creer.error.message : t('commun:erreurReseau')}
            </p>
          )}
        </div>
      </Dialog>
    </div>
  );
}
